/**
 * Till redemption for weekly meal-plan bookings.
 */

import { doc, runTransaction } from 'firebase/firestore';
import { getFirestoreDB } from './firebase';
import type { MealPeriod, WeeklyBooking } from '../types';
import { mealPeriodText, nzDateKey } from './mealPlanWeek';

/** Mark a booked day/period as redeemed (transactional). */
export async function redeemWeeklyBookingOnPos(params: {
  bookingId: string;
  orderId: string;
  staffId?: string | number;
}): Promise<WeeklyBooking> {
  const db = getFirestoreDB();
  const ref = doc(db, 'meal_plan_bookings', params.bookingId);
  const now = Date.now();
  const today = nzDateKey(now);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Meal plan booking not found.');
    const data = { id: snap.id, ...snap.data() } as WeeklyBooking;
    if (data.status !== 'booked') {
      throw new Error(`Booking is ${data.status}, cannot redeem.`);
    }
    if (data.dayKey !== today) {
      throw new Error('Can only redeem today’s booking.');
    }
    const next: WeeklyBooking = {
      ...data,
      status: 'redeemed',
      redeemedAt: now,
      redeemedOrderId: params.orderId,
      redeemedByStaffId: params.staffId,
      updatedAt: now,
    };
    tx.set(ref, next, { merge: true });
    return next;
  });
}

/** Staff pickup from the meal plan screen. One meal or several, including other days in the same visit. */
export async function markMealPlanPickups(params: {
  bookingIds: string[];
  staffId?: string | number;
}): Promise<number> {
  const db = getFirestoreDB();
  const now = Date.now();
  let marked = 0;
  for (const bookingId of params.bookingIds) {
    const ref = doc(db, 'meal_plan_bookings', bookingId);
    const did = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return false;
      const data = snap.data() || {};
      if (data.status !== 'booked') return false;
      tx.set(
        ref,
        {
          status: 'redeemed',
          redeemedAt: now,
          redeemedByStaffId: params.staffId ?? null,
          updatedAt: now,
        },
        { merge: true },
      );
      return true;
    });
    if (did) marked += 1;
  }
  return marked;
}

/** Apply pickup switches from the week list. Picked-up meals are marked; switched off meals go back to still to collect. */
export async function applyMealPlanPickupToggles(params: {
  pickUpIds: string[];
  undoIds: string[];
  staffId?: string | number;
}): Promise<{ pickedUp: number; cleared: number }> {
  const db = getFirestoreDB();
  const now = Date.now();
  let pickedUp = 0;
  let cleared = 0;

  for (const bookingId of params.pickUpIds) {
    const ref = doc(db, 'meal_plan_bookings', bookingId);
    const did = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return false;
      const data = snap.data() || {};
      if (data.status !== 'booked') return false;
      tx.set(
        ref,
        {
          status: 'redeemed',
          redeemedAt: now,
          redeemedByStaffId: params.staffId ?? null,
          updatedAt: now,
        },
        { merge: true },
      );
      return true;
    });
    if (did) pickedUp += 1;
  }

  for (const bookingId of params.undoIds) {
    const ref = doc(db, 'meal_plan_bookings', bookingId);
    const did = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return false;
      const data = snap.data() || {};
      if (data.status !== 'redeemed') return false;
      tx.set(
        ref,
        {
          status: 'booked',
          redeemedAt: null,
          redeemedByStaffId: null,
          redeemedOrderId: null,
          updatedAt: now,
        },
        { merge: true },
      );
      return true;
    });
    if (did) cleared += 1;
  }

  return { pickedUp, cleared };
}

/** One till line for the written meal. Price stays 0; the customer already paid the week. */
export function messItemsForPeriod(
  day: Parameters<typeof mealPeriodText>[0],
  period: MealPeriod,
): { name: string; menuItemId: string; price: number; quantity: number; notes: string }[] {
  const name = mealPeriodText(day, period);
  if (!name) return [];
  return [{
    name,
    menuItemId: '',
    price: 0,
    quantity: 1,
    notes: `Meal plan · ${period}`,
  }];
}

/** @deprecated pack/$cap helper */
export function mealPlanCoverDollars(maxPerMealCents: number, orderSubtotalDollars: number): number {
  const cap = Math.max(0, maxPerMealCents) / 100;
  return Math.min(cap, Math.max(0, orderSubtotalDollars));
}
